import React, { useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  Text,
} from '@televet/kibble-ui';
import { useApolloClient, gql } from '@apollo/client';
import { useUpdateTeamChannel } from '../hooks/useUpdateTeamChannel';
import { Channel, ChannelVisibility } from 'shared/types/graphql';

interface IArchiveChannelAlertDialogProps {
  channelId: string | null;
  onClose: () => void;
  channelVisibility: ChannelVisibility;
}

const ArchiveChannelAlertDialog = ({
  channelId,
  onClose,
  channelVisibility,
}: IArchiveChannelAlertDialogProps): JSX.Element => {
  const apolloClient = useApolloClient();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const isDirectMessage = channelVisibility === ChannelVisibility.DirectMessage;

  const [updateTeamChannel, { loading: isUpdatingTeamChannel }] = useUpdateTeamChannel();

  const handleArchiveChannelClick = useCallback(async () => {
    if (!channelId) return;
    await updateTeamChannel({ variables: { where: { id: channelId }, data: { isActive: false } } });
    onClose();
  }, [channelId, updateTeamChannel, onClose]);

  const channel = apolloClient.readFragment<Channel>({
    fragment: gql`
      fragment TeamChannel on Channel {
        friendlyName
      }
    `,
    id: `Channel:${channelId}`,
  });

  return (
    <AlertDialog isOpen={!!channelId} isCentered={true} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="xl" fontWeight="bold">
            {isDirectMessage ? 'Close this conversation?' : 'Archive this team channel?'}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text>
              Your team members will no longer be able to see or participate in the <b>{channel?.friendlyName || ''}</b>{' '}
              {isDirectMessage ? 'conversation' : 'channel'}.
            </Text>
            <Text paddingTop="4">
              {isDirectMessage
                ? ' Once you close this conversation, it can be re-opened by starting a new direct message.'
                : ' Once you archive this channel, it can be un-archived by reaching out to Otto support.'}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button variant="tertiary" ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primaryDestructive"
              isLoading={isUpdatingTeamChannel}
              onClick={handleArchiveChannelClick}
              ml={3}
            >
              {isDirectMessage ? 'Close Conversation' : 'Archive Channel'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default ArchiveChannelAlertDialog;
